<template>
  <div class="app">
    <nav v-if="auth.isLoggedIn && !isWidget && !auth.isMaster" class="navbar">
      <div class="nav-brand">
        <h1><img src="/logo/qclient-mark.svg" alt="" class="brand-mark" />QClient</h1>
        <button 
          v-if="isMobile" 
          class="burger-btn" 
          @click="mobileMenuOpen = !mobileMenuOpen"
          :class="{ 'open': mobileMenuOpen }"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
      
      <div class="nav-links" :class="{ 'mobile-open': mobileMenuOpen }">
        <!-- Записи -->
        <router-link to="/" class="nav-item" @click="mobileMenuOpen = false">
          <span class="nav-icon"><AppIcon name="calendar" /></span>
          <span class="nav-label">Записи</span>
          <span v-if="bookings.pendingCount > 0" class="badge badge-pending">
            {{ bookings.pendingCount }}
          </span>
        </router-link>

                <!-- Клиенты -->
        <router-link to="/clients" class="nav-item" @click="mobileMenuOpen = false">
          <span class="nav-icon"><AppIcon name="users" /></span>
          <span class="nav-label">Клиенты</span>
        </router-link>
        
        <router-link to="/settings" class="nav-item" @click="mobileMenuOpen = false">
          <span class="nav-icon"><AppIcon name="settings" /></span>
          <span class="nav-label">Настройки</span>
        </router-link>
        
        <router-link to="/analytics" class="nav-item" @click="mobileMenuOpen = false">
          <span class="nav-icon"><AppIcon name="chart" /></span>
          <span class="nav-label">Аналитика</span>
          <span v-if="locked('analytics_extended')" class="nav-lock">тариф</span>
        </router-link>

        <router-link v-if="['owner', 'admin'].includes(auth.userRole)" to="/reports/impact" class="nav-item" @click="mobileMenuOpen = false">
          <span class="nav-icon"><AppIcon name="chart" /></span>
          <span class="nav-label">Что принесла система</span>
          <span v-if="locked('analytics_extended')" class="nav-lock">тариф</span>
        </router-link>

        <router-link v-if="['owner', 'admin'].includes(auth.userRole)" to="/reports/plan-fact" class="nav-item" @click="mobileMenuOpen = false">
          <span class="nav-icon"><AppIcon name="clock" /></span>
          <span class="nav-label">План и факт</span>
          <span v-if="locked('analytics_extended')" class="nav-lock">тариф</span>
        </router-link>

        <router-link v-if="['owner', 'admin'].includes(auth.userRole)" to="/resources-day" class="nav-item" @click="mobileMenuOpen = false">
          <span class="nav-icon"><AppIcon name="box" /></span>
          <span class="nav-label">Ресурсы по дням</span>
          <span v-if="locked('resource_planning')" class="nav-lock">тариф</span>
        </router-link>

        <router-link v-if="auth.userRole === 'owner'" to="/payroll" class="nav-item" @click="mobileMenuOpen = false">
          <span class="nav-icon"><AppIcon name="wallet" /></span>
          <span class="nav-label">Зарплата</span>
          <span v-if="locked('analytics_extended')" class="nav-lock">тариф</span>
        </router-link>
        
        <router-link v-if="auth.userRole === 'owner'" to="/inventory" class="nav-item" @click="mobileMenuOpen = false">
          <span class="nav-icon"><AppIcon name="box" /></span>
          <span class="nav-label">Склад</span>
          <span v-if="locked('tools_pack')" class="nav-lock">тариф</span>
        </router-link>

        <router-link 
          :to="auth.companyId ? `/widget/${auth.companyId}` : '/widget'" 
          class="nav-item"
          @click="mobileMenuOpen = false"
        >
          <span class="nav-icon"><AppIcon name="link" /></span>
          <span class="nav-label">Виджет записи</span>
        </router-link>
        
        <!-- Мобильный выход -->
        <div v-if="isMobile" class="mobile-user">
          <router-link to="/settings/profile" class="user-name">{{ auth.userName }}</router-link>
          <router-link 
            v-if="company.currentTariff" 
            to="/pricing" 
            class="tariff-badge"
            :class="`tariff-${company.currentTariff.tariff_code || 'free'}`"
          >
            {{ tariffLabel }}
          </router-link>
          <button @click="logout">Выйти</button>
        </div>
      </div>
      
      <div v-if="!isMobile" class="user-area" ref="userMenuRef">
        <router-link 
          v-if="company.currentTariff" 
          to="/pricing" 
          class="tariff-badge"
          :class="`tariff-${company.currentTariff.tariff_code || 'free'}`"
          :title="tariffTooltip"
        >
          {{ tariffLabel }}
        </router-link>

        <button
          ref="userTriggerRef"
          type="button"
          class="user-trigger"
          :class="{ open: userMenuOpen }"
          :aria-expanded="userMenuOpen"
          aria-haspopup="menu"
          @click="toggleUserMenu"
        >
          <span class="user-avatar">{{ userInitial }}</span>
          <span class="user-trigger-name">{{ auth.userName }}</span>
          <AppIcon name="chevron-down" :size="14" />
        </button>

        <!-- В <body>: у .navbar overflow-x:auto, из-за этого overflow-y тоже становится auto,
             и выпадающее меню обрезалось до «маленькой полоски» -->
        <Teleport to="body">
        <div v-if="userMenuOpen" ref="userMenuPanelRef" class="user-menu" :style="userMenuStyle" role="menu">
          <div class="um-head">{{ auth.userName }}</div>
          <router-link to="/settings/profile" class="um-item" role="menuitem" @click="userMenuOpen = false">
            <AppIcon name="user" :size="16" /> Профиль
          </router-link>
          <router-link v-if="company.currentTariff" to="/pricing" class="um-item" role="menuitem" @click="userMenuOpen = false">
            <AppIcon name="wallet" :size="16" /> Тариф и оплата
          </router-link>
          <div class="um-sep"></div>
          <button type="button" class="um-item um-danger" role="menuitem" @click="userMenuOpen = false; logout()">
            <AppIcon name="logout" :size="16" /> Выйти
          </button>
        </div>
        </Teleport>
      </div>
    </nav>
    
    <router-view />
    <ToastContainer />
    
    <!-- Модалка Waitlist -->
    <WaitlistModal 
      v-if="showWaitlistModal" 
      @close="showWaitlistModal = false"
    />
  </div>
</template>

<script setup>
import AppIcon from './components/AppIcon.vue'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from './stores/auth'
import { useCompanyStore } from './stores/company'
import { useBookingsStore } from './stores/bookings'
import { useWaitlistStore } from './stores/waitlist'
import { useTariffInfoStore } from './stores/tariffInfo'
import WaitlistModal from './components/WaitlistModal.vue'
import ToastContainer from './components/ToastContainer.vue'

const router = useRouter()
const route = useRoute()
const auth = useAuthStore()
const company = useCompanyStore()
const bookings = useBookingsStore()
const waitlist = useWaitlistStore()
const tariffInfo = useTariffInfoStore()
const locked = (feature) => tariffInfo.loaded && !tariffInfo.has(feature)

const isWidget = computed(() => route.path.startsWith('/widget'))
const isMobile = ref(false)
const mobileMenuOpen = ref(false)
const showWaitlistModal = ref(false)

// === МЕНЮ ПОЛЬЗОВАТЕЛЯ ===
const userMenuOpen = ref(false)
const userMenuRef = ref(null)
const userTriggerRef = ref(null)
const userMenuPanelRef = ref(null)
const userMenuStyle = ref({})

// Меню рисуется в <body> с position: fixed — считаем координаты по кнопке
function placeUserMenu() {
  const el = userTriggerRef.value
  if (!el) return
  const r = el.getBoundingClientRect()
  userMenuStyle.value = {
    top: `${Math.round(r.bottom + 8)}px`,
    right: `${Math.max(8, Math.round(window.innerWidth - r.right))}px`
  }
}

function toggleUserMenu() {
  if (!userMenuOpen.value) placeUserMenu()
  userMenuOpen.value = !userMenuOpen.value
}

const closeUserMenu = () => { userMenuOpen.value = false }
const userInitial = computed(() => (auth.userName || '?').trim().charAt(0).toUpperCase())

const onDocPointer = (e) => {
  if (!userMenuOpen.value) return
  const inside = (userMenuRef.value && userMenuRef.value.contains(e.target)) ||
                 (userMenuPanelRef.value && userMenuPanelRef.value.contains(e.target))
  if (!inside) userMenuOpen.value = false
}
const onDocKey = (e) => { if (e.key === 'Escape') userMenuOpen.value = false }
onMounted(() => {
  document.addEventListener('mousedown', onDocPointer)
  document.addEventListener('keydown', onDocKey)
  window.addEventListener('resize', closeUserMenu)
  window.addEventListener('scroll', closeUserMenu, true)
})
onUnmounted(() => {
  document.removeEventListener('mousedown', onDocPointer)
  document.removeEventListener('keydown', onDocKey)
  window.removeEventListener('resize', closeUserMenu)
  window.removeEventListener('scroll', closeUserMenu, true)
})

// === ТАРИФ ===
const tariffLabel = computed(() => {
  const t = company.currentTariff
  if (!t) return ''
  
  const code = t.tariff_code || t.tariff || 'free'
  const name = t.tariff_name || code
  
  // Trial показываем ТОЛЬКО если is_trial=true И есть trial_ends_at в будущем
  if (t.is_trial && t.trial_ends_at) {
    const days = Math.ceil((new Date(t.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24))
    if (days > 0) {
      return `${name} (Trial: ${days} дн.)`
    }
  }
  
  if (t.is_expired) {
    return `${name} (Истёк)`
  }
  
  if (t.expires_at) {
    const days = Math.ceil((new Date(t.expires_at) - new Date()) / (1000 * 60 * 60 * 24))
    if (days <= 7 && days > 0) {
      return `${name} (${days} дн.)`
    }
  }
  
  return name
})

const isWaitlistAvailable = computed(() => {
  // Если тариф не загружен — показываем кнопку (проверка на бэкенде)
  if (!company.currentTariff) return true
  
  const tariff = company.currentTariff
  
  // Проверка 1: поле waitlist в features
  if (tariff.features?.waitlist !== undefined) {
    return tariff.features.waitlist === true
  }
  
  // Проверка 2: поле waitlist на верхнем уровне
  if (tariff.waitlist !== undefined) {
    return tariff.waitlist === true
  }
  
  // Проверка 3: по коду тарифа — Free без waitlist, остальные с waitlist
  const code = tariff.tariff_code || tariff.code || 'free'
  if (code === 'free') {
    // Free + trial = показываем
    if (tariff.is_trial || tariff.trial_days_left > 0) return true
    return false
  }
  
  // Pro, Business, Enterprise — waitlist доступен
  return true
})

const tariffTooltip = computed(() => {
  const t = company.currentTariff
  if (!t) return ''
  if (t.is_expired) return 'Подписка истекла. Нажмите для продления.'
  if (t.is_trial) return 'Пробный период. Нажмите для выбора тарифа.'
  return 'Нажмите для управления подпиской'
})

function checkMobile() {
  isMobile.value = window.innerWidth <= 768
  if (!isMobile.value) mobileMenuOpen.value = false
}

watch(() => auth.isLoggedIn, (v) => { if (v && !auth.isMaster) tariffInfo.load(true); else tariffInfo.reset() })

onMounted(() => {
  if (auth.isLoggedIn && !auth.isMaster) tariffInfo.load()
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})

const logout = () => {
  mobileMenuOpen.value = false
  if (pollInterval) clearInterval(pollInterval)
  auth.logout()
  window.location.replace('/login')
}

function openWaitlistModal() {
  showWaitlistModal.value = true
  mobileMenuOpen.value = false
}

// Polling для автообновления бейджа
let pollInterval = null

onMounted(async () => {
  if (auth.token) {
    await auth.fetchUser()
    // кабинет мастера работает со своими данными: общие справочники и счётчики администратора ему не нужны (там 403)
    if (auth.isLoggedIn && !auth.isMaster) {
      await company.loadAll()
      await Promise.all([
        bookings.loadPending(),
        bookings.loadPendingCount(),
        waitlist.loadCounts(),
        company.loadCurrentTariff()
      ])
      
      pollInterval = setInterval(() => {
        if (document.hidden) return
        bookings.loadPending()
        bookings.loadPendingCount()
        waitlist.loadCounts()
      }, 30000)
    }
  }
})

onUnmounted(() => {
  if (pollInterval) clearInterval(pollInterval)
})
</script>

<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; background: #f3f4f6; color: #0f172a; }

/* ========== НАВБАР ========== */
.navbar {
  background: #ffffff;
  color: var(--brand-navy);
  border-bottom: 1px solid #e5e7eb;
  padding: 0.6rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  z-index: 100;
  min-width: 0;
  overflow-x: auto;
  overflow-y: visible;
  scrollbar-width: none;
}

.navbar::-webkit-scrollbar {
  display: none;
}

/* ========== ЛОГОТИП ========== */
.nav-brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.navbar h1 {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--brand-navy);
  white-space: nowrap;
}

.brand-mark { height: 1.5rem; width: auto; display: block; }

/* ========== МЕНЮ (центр) ========== */
.nav-links {
  display: flex;
  gap: 0.25rem;
  flex: 1 1 auto;
  min-width: 0;
  align-items: center;
  overflow: visible;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.4rem 0.6rem;
  border-radius: 8px;
  color: #475569;
  text-decoration: none;
  font-size: 0.88rem;
  font-weight: 500;
  transition: all 0.2s;
  cursor: pointer;
  background: transparent;
  border: none;
  white-space: nowrap;
  position: relative;
  flex-shrink: 0;
}

.nav-item:hover {
  color: var(--brand-navy);
  background: #f1f5f9;
}

.nav-item.router-link-active {
  color: var(--brand-navy);
  background: var(--brand-primary-soft);
  font-weight: 600;
}

/* зелёная отметка активного раздела — акцент бренда */
.nav-item.router-link-active::after {
  content: '';
  position: absolute;
  left: 0.6rem;
  right: 0.6rem;
  bottom: -0.55rem;
  height: 3px;
  border-radius: 3px 3px 0 0;
  background: var(--brand-green);
}

.nav-icon {
  display: inline-flex;
  align-items: center;
  line-height: 1;
}

.nav-label {
  font-size: 0.85rem;
}

.nav-item-btn {
  font-family: inherit;
}

/* ========== БЕЙДЖИ ========== */
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 0.35rem;
  border-radius: 9px;
  font-size: 0.75rem;
  font-weight: 700;
  color: white;
  animation: pulse 2s infinite;
  margin-left: 0.2rem;
}

.badge-pending {
  background: #ef4444;
}

.badge-waitlist {
  background: #f59e0b;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.15); }
}

/* ========== ПРАВЫЙ БЛОК (пользователь) ========== */
.user {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: auto;  /* ← прижимаем вправо */
}

.user-name {
  color: #475569;
  text-decoration: none;
  font-size: 0.8rem;
  transition: color 0.2s;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-name:hover {
  color: var(--brand-navy);
}

.user button {
  background: transparent;
  border: 1px solid #cbd5e1;
  color: #475569;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.2s;
}

.user button:hover {
  color: var(--brand-navy);
  border-color: var(--brand-navy);
}

/* ========== БЕЙДЖ ТАРИФА ========== */
.nav-lock {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  background: #fef3c7;
  color: #92400e;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.5;
}

.tariff-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.6rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s;
  white-space: nowrap;
  cursor: pointer;
  flex-shrink: 0;
}

.tariff-badge:hover {
  filter: brightness(1.15);
  transform: scale(1.05);
}

.tariff-free {
  background: #eef2f7;
  color: #475569;
}

.tariff-trial {
  background: #f59e0b;
  color: white;
}

.tariff-pro {
  background: var(--brand-primary);
  color: white;
}

.tariff-business {
  background: var(--brand-accent);
  color: white;
}

.tariff-expired {
  background: #ef4444;
  color: white;
  animation: pulse 2s infinite;
}

/* ========== БУРГЕР ========== */
.burger-btn {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  flex-shrink: 0;
}

.burger-btn span {
  display: block;
  width: 100%;
  height: 2px;
  background: #475569;
  border-radius: 2px;
  transition: all 0.3s;
}

.burger-btn.open span:nth-child(1) {
  transform: rotate(45deg) translate(5px, 5px);
  background: var(--brand-navy);
}

.burger-btn.open span:nth-child(2) {
  opacity: 0;
}

.burger-btn.open span:nth-child(3) {
  transform: rotate(-45deg) translate(5px, -5px);
  background: var(--brand-navy);
}

/* ========== МЕДИА: 1200px — скрываем текст меню, оставляем иконки ========== */
@media (max-width: 1200px) {
  .navbar {
    padding: 0.5rem 0.75rem;
    gap: 0.5rem;
  }
  
  .nav-label {
    display: none;
  }
  
  .nav-item {
    padding: 0.4rem;
    gap: 0;
  }
  
  .user-name {
    max-width: 60px;
    font-size: 0.75rem;
  }
  
  .tariff-badge {
    font-size: 0.75rem;
    padding: 0.15rem 0.4rem;
  }
  
  .user button {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
  }
}

/* ========== МЕДИА: 900px — убираем имя пользователя, оставляем только иконку+тариф+выйти ========== */
@media (max-width: 900px) {
  .user-name {
    display: none;
  }
  
  .tariff-badge {
    font-size: 0.75rem;
    padding: 0.2rem 0.4rem;
  }
}

/* ========== МЕДИА: 768px (мобильная) ========== */
@media (max-width: 768px) {
  .navbar {
    padding: 0.75rem 1rem;
    flex-wrap: nowrap;
    overflow: visible;
  }

  .burger-btn {
    display: flex;
    margin-left: auto;  /* бургер справа */
  }

  .nav-links {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #ffffff;
    flex-direction: column;
    padding: 0.5rem;
    gap: 0.25rem;
    border-top: 1px solid #e5e7eb;
    z-index: 1000;
    box-shadow: 0 10px 20px rgba(3, 28, 56, 0.12);
  }

  .nav-links.mobile-open {
    display: flex;
  }

  .nav-item {
    width: 100%;
    padding: 0.75rem 1rem;
    font-size: 1rem;
    gap: 0.4rem;
  }

  .nav-item:hover,
  .nav-item.router-link-active {
    background: var(--brand-primary-soft);
  }

  .nav-item.router-link-active::after { display: none; }

  .nav-icon {
    width: 24px;
    justify-content: center;
  }

  .nav-label {
    display: inline;
    font-size: 1rem;
  }

  .badge {
    margin-left: auto;
    animation: none;
  }

  .user {
    display: none;
  }

  .mobile-user {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    border-top: 1px solid #e5e7eb;
    margin-top: 0.5rem;
  }

  .mobile-user span {
    color: #475569;
    font-size: 0.9rem;
  }

  .mobile-user button {
    background: transparent;
    border: 1px solid #cbd5e1;
    color: #475569;
    padding: 0.4rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.85rem;
  }
}

/* ========== KANBAN DND ГЛОБАЛЬНЫЕ ========== */
.kanban-dragging,
.kanban-dragging * {
  cursor: grabbing !important;
}

.sortable-ghost {
  opacity: 0.4 !important;
  background: #dbe5f1 !important;
  border: 2px dashed var(--brand-primary) !important;
  box-shadow: none !important;
}

.sortable-drag {
  opacity: 0.95 !important;
  transform: rotate(2deg) scale(1.03) !important;
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.25) !important;
  z-index: 9999 !important;
}

.sortable-chosen {
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.kanban-column.drag-over .column-body {
  background: rgba(3, 28, 56, 0.05);
}

/* ========== МЕНЮ ПОЛЬЗОВАТЕЛЯ ========== */
.user-area {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-left: auto;
  flex-shrink: 0;
  white-space: nowrap;
}

.user-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.6rem 0.25rem 0.3rem;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: white;
  color: #334155;
  font: inherit;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.user-trigger:hover,
.user-trigger.open { background: var(--brand-primary-soft); border-color: #c4d3e6; }

.user-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--brand-primary);
  color: white;
  font-size: 0.8rem;
  font-weight: 700;
}

.user-trigger-name { max-width: 140px; overflow: hidden; text-overflow: ellipsis; }

.user-menu {
  position: fixed;   /* top/right задаются из скрипта по положению кнопки */
  min-width: 220px;
  z-index: 1200;
  padding: 0.35rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: white;
  box-shadow: 0 12px 32px rgba(3, 28, 56, 0.16);
}

.um-head { padding: 0.5rem 0.65rem; font-size: 0.8rem; color: #64748b; border-bottom: 1px solid #f1f5f9; margin-bottom: 0.25rem; }

.um-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  padding: 0.55rem 0.65rem;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #1e293b;
  font: inherit;
  font-size: 0.88rem;
  text-decoration: none;
  text-align: left;
  cursor: pointer;
}

.um-item:hover { background: var(--brand-primary-soft); }
.um-sep { height: 1px; margin: 0.3rem 0; background: #f1f5f9; }
.um-danger { color: #b91c1c; }
.um-danger:hover { background: #fef2f2; }

@media (max-width: 1200px) {
  .user-trigger-name { display: none; }
  .user-trigger { padding-right: 0.45rem; }
}
</style>